import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

// Components
import { isValidEmail } from '../components/validation';
import axiosInstance, { Logout, TokenManager } from '../components/axios';
import Toast from '../components/toast';

// Static resources
import bgimage from '../images/login-page.jpg';

//MaterialUI
import Avatar from '@mui/material/Avatar';
import Backdrop from '@mui/material/Backdrop';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import CssBaseline from '@mui/material/CssBaseline';
import Grid from '@mui/material/Grid';
import Link from '@mui/material/Link';
import Paper from '@mui/material/Paper';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';

import PinIcon from '@mui/icons-material/Pin';

export default function PasswordReset() {

	const navigate = useNavigate();

	// Form State
	const [loading, setLoading] = useState(false);
	const [passEmailSent, setPassEmailSent] = useState(false);
	const [toast, setToast] = useState({
		open: false,
		severity: 'info',
		message: '',
		duration: 3000,
		clickToClose: false,
	});

	// Email State
	const [email, setEmail] = useState('');
	const [emailValid, setEmailValid] = useState(false);

	// Form Validity Checks
	useEffect(() => { setEmailValid(isValidEmail(email)) }, [email]);

	// Handle Clearing local storage
	useEffect(() => {
		let refreshTokenValid = TokenManager.isRefreshValid();
		if (!refreshTokenValid) {
			Logout();
		}
	}, []);

	// Navigate to the specified next page, or the locations list, after successful login
	const navigateToLoginPage = () => navigate('/login');

	// Handle requesting a password reset token
	function handleTokenRequest() {
		if (emailValid) {
			setLoading(true);
			axiosInstance.post(`password_reset/`, {
					email: email,
				})
				.then((res) => {})
				.catch((error) => {})
				.finally(() => {
					// Set the email sent flag to true, even if the user's email isn't found, for security reasons
					setPassEmailSent(true);
					setLoading(false);
				});
		} else {
			setToast({ open: true, severity: 'error', message: 'Invalid email address. Please enter a valid email address', duration: 3000, clickToClose: true });
		}
	}
	
	// Sign in form submittal
	const handleEmailSubmit = (e) => {
		e.preventDefault();
		handleTokenRequest();
	};

	// Cancel button handler
	const handleLoginLinkClick = () => {
		setEmail('');
		setPassEmailSent(false);
		navigateToLoginPage();
	}
	
	const passResetRequestForm = (
		<Box
			sx={{
				my: 8,
				mx: 4,
				display: 'flex',
				flexDirection: 'column',
				alignItems: 'center',
			}}
		>
			<Avatar sx={{ m: 1, bgcolor: '#B1282E' }}>
				<PinIcon />
			</Avatar>
			<Typography component='h1' variant='h5'>
				Password Reset
			</Typography>
			<Box component='form' onSubmit={handleEmailSubmit} sx={{ mt: 1 }}>
				<Grid container spacing={1}>
					<Grid item xs={12}>
						<Typography variant='body1' component='div' sx={{ textAlign: 'center' }}>
							Enter your OBVM2 email address to send a password reset request.
						</Typography>
					</Grid>
					<Grid item xs={12}>
						<TextField
							id='email'
							name='email'
							label='Email Address'
							autoComplete='email'
							required
							value={email}
							onChange={(event) => setEmail(event.target.value)}
							inputProps={{ maxLength: 64 }}
							error={email.length > 0 && !emailValid}
							helperText={(email && !emailValid) ? 'Invalid email address' : ''}
							variant='outlined'
							margin='normal'
							fullWidth
							autoFocus
						/>
					</Grid>
					<Grid item xs={12}>
						<Button
							fullWidth
							variant='contained'
							sx={{ mt: 1, mb: 2 }}
							type='submit'
						>
							Reset Password
						</Button>
					</Grid>
					<Grid item xs={12}>
						<Link onClick={handleLoginLinkClick} variant='body2' style={{ cursor: 'pointer' }}>
							Back to Login
						</Link>
					</Grid>
				</Grid>
			</Box>
		</Box>
	);
	
	const emailSentForm = (
		<Box
			sx={{
				my: 8,
				mx: 4,
				display: 'flex',
				flexDirection: 'column',
				alignItems: 'center',
				spacing: 2,
			}}
		>
			<Avatar sx={{ m: 1, bgcolor: '#B1282E' }}>
				<PinIcon />
			</Avatar>
			<Typography component='h1' variant='h5'>
				Password Reset Email Sent
			</Typography>
			<Typography variant='body1' component='div' sx={{ textAlign: 'center', m: 2,  }}>
				A password reset link has been sent to { email }. Please follow the instructions in the email to reset your password.
			</Typography>
			<Typography variant='body1' component='div' sx={{ textAlign: 'center' }}>
				The email may take a few minutes to show up in your inbox.
			</Typography>
			<Box sx={{ padding: 3 }}>
				<Button onClick={handleLoginLinkClick} variant='contained' size='large' style={{ cursor: 'pointer' }}>
					Back to Login
				</Button>
			</Box>
		</Box>
	);

	return (		
		<Grid container component='main' sx={{ height: '100vh' }}>
			<CssBaseline />
			<Toast
				severity={toast.severity}
				message={toast.message}
				toastOpen={toast.open}
				setToastOpen={() => setToast({ ...toast, open: false })}
				duration={toast.duration}
				clickToClose={toast.clickToClose}
			/>
			<Backdrop
				sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}
				open={loading}
			>
				<CircularProgress color="inherit" />
			</Backdrop>
			<Grid item xs={false} sm={4} md={6} lg={8} sx={{
				backgroundImage: `url(${bgimage})`,
				backgroundRepeat: 'no-repeat',
				backgroundColor: (t) =>
				t.palette.mode === 'light' ? t.palette.grey[50] : t.palette.grey[900],
				backgroundSize: 'cover',
				backgroundPosition: '5%',
			}} />
			<Grid item xs={12} sm={8} md={6} lg={4} component={Paper} elevation={6} square>
				{ passEmailSent ? emailSentForm : passResetRequestForm }
			</Grid>
		</Grid>
	);
}